/*
By default, Klaro will load the config from a global 'klaroConfig' variable. You
can change this by specifying the 'data-klaro-config' attribute on your script:
<script src="klaro.js" data-klaro-config="myConfigVariableName"
*/
import * as klaro from 'klaro';
import { AdCampaignDataToCookie, getCookie, loadScript } from './components/tools';

const COOKIE_DOMAIN = __FULL_WEBSITE_URL__.split('://')[1].split(':')[0]; // Extracting the domain from the full URL
const COOKIE_NAME = 'cookies_consent';
const COOKIE_POLICY_URL_PL = '/polityka-cookies'; // Hardcoded URL for the cookie policy
const LEARN_MORE_PL = 'Dowiedz się więcej';
const LEARN_MORE_EN = 'Learn more';

function getCookiePolicyElement(text, url) {
  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
}
function getIternalCookiePolicyElement(text) {
  return `<a class="show-popup text-danger" data-target="${COOKIE_POLICY_URL_PL}">${text}</a>`;
}

const klaroConfig = {
  /*
  Setting 'testing' to 'true' will cause Klaro to not show the consent notice or
  modal by default, except if a special hash tag is appended to the URL (#klaro-
  testing). This makes it possible to test Klaro on your live website without
  affecting normal visitors.
  */
  testing: false,

  // Lang is set after this const, so its content can be dynamically set based on the user's language.
  // lang: 'pl',

  /*
  You can customize the title of the consent modal. By default, Klaro will hide the
  modal title, but you can set 'showNoticeTitle' to 'true' to show it.
   */
  showNoticeTitle: true,

  /*
  You can toggle the display of the powered by Klaro link in the consent modal.
   */
  disablePoweredBy: true,

  /*
  You can customize the ID of the DIV element that Klaro will create when starting
  up. By default, Klaro will use 'klaro'.
  */
  elementID: 'klaro',

  /*
  You can customize how Klaro persists consent information in the browser. Specify
  either cookie' (the default) or 'localStorage'.
  */
  storageMethod: 'cookie',

  /*
  You can customize the name of the cookie or localStorage entry that Klaro will
  use for storing the consent information. By default, Klaro will use 'klaro'.
  */
  storageName: COOKIE_NAME,

  /*
  If set to `true`, Klaro will render the texts given in the
  `consentModal.description` and `consentNotice.description` translations as HTML.
  This enables you to e.g. add custom links or interactive content.
  */
  htmlTexts: true,

  /*
  You can change the cookie domain for the consent manager itself. Use this if you
  want to get consent once for multiple matching domains. By default, Klaro will
  use the current domain. Only relevant if 'storageMethod' is set to 'cookie'.
  */
  cookieDomain: COOKIE_DOMAIN,

  /*
  You can also set a custom expiration time for the Klaro cookie. By default, it
  will expire after 30 days. Only relevant if 'storageMethod' is set to 'cookie'.
  */
  cookieExpiresAfterDays: 365,

  /*
  Defines the default state for services in the consent modal (true=enabled by
  default). You can override this setting in each service.
  */
  default: false,

  /*
  If 'mustConsent' is set to 'true', Klaro will directly display the consent
  manager modal and not allow the user to close it before having actively
  consented or declined the use of third-party services.
  */
  mustConsent: false,

  /*
  Setting 'acceptAll' to 'true' will show an "accept all" button in the notice and
  modal, which will enable all third-party services if the user clicks on it. If
  set to 'false', there will be an "accept" button that will only enable the
  services that are enabled in the consent modal.
  */
  acceptAll: true,

  /*
  Setting 'hideDeclineAll' to 'true' will hide the "decline" button in the consent
  modal and force the user to open the modal in order to change his/her consent or
  disable all third-party services. We strongly advise you to not use this
  feature, as it opposes the "privacy by default" and "privacy by design"
  principles of the GDPR (but might be acceptable in other legislations such as
  under the CCPA)
  */
  hideDeclineAll: true,

  /*
  Setting 'hideLearnMore' to 'true' will hide the "learn more / customize" link in
  the consent notice. We strongly advise against using this under most
  circumstances, as it keeps the user from customizing his/her consent choices.
  */
  hideLearnMore: false,

  /*
  You can overwrite existing translations and add translations for your service
  descriptions and purposes. See `src/translations/` for a full list of
  translations that can be overwritten:
  https://github.com/KIProtect/klaro/tree/master/src/translations
  */
  translations: {
    /*
        The `zz` key contains default translations that will be used as fallback values.
            This can e.g. be useful for defining a fallback privacy policy URL.
        */
    en: {
      ok: 'Accept all cookies',
      consentNotice: {
        title: 'We care about your privacy',
        description: `
          <p>We use cookies and similar technologies provided by us and our partners. This involves saving, storing, or accessing information already stored in your web browser.</p>
          <p>We use these technologies to enable you to use the Pomagam.pl platform properly, including securely and efficiently (these are known as essential cookies). ${getIternalCookiePolicyElement(LEARN_MORE_EN)}</p>
          <p>Additionally, we use optional cookies and similar technologies for the purposes listed below, for which you may choose to give or withhold your consent. You can change your choices at any time by visiting the “Manage cookies consent” functionality on Pomagam.pl.<br>We use optional cookies for the following purposes:</p>
          <ol>
            <li><b>Analytics</b> – We use so-called analytical cookies to improve the functioning of the Pomagam.pl platform. Thanks to these cookies, we know how you use our platform, for example, where you come from and how much time you spend on the site. This allows us to create and analyze specific statistics. ${getIternalCookiePolicyElement(LEARN_MORE_EN)}</li>
            <li><b>Retargeting</b> – Both we and our trusted partners use cookies and similar technologies to target users with specific, including personalized, content related to Pomagam.pl, especially about ongoing fundraisers. Such content is displayed not only on the Pomagam.pl platform but also on other websites. ${getIternalCookiePolicyElement(LEARN_MORE_EN)}</li>
          </ol>
        `,
        learnMore: 'Customize my selection',
      },
      consentModal: {
        title: 'Adjust settings',
        description: 'You can change or withdraw your Optional Cookie consent at any time.',
      },
      privacyPolicyUrl: COOKIE_POLICY_URL_PL,
      privacyPolicy: {
        name: 'Pomagam.pl privacy policy',
        text: 'More information can be found in the {privacyPolicy}',
      },
      purposes: {
        internal: {
          title: 'Essential cookies',
          description: 'These are necessary for our website to function properly and cannot be disabled. We also use data from these cookies to ensure your security and to detect errors on the site.',
        },
        analytics: {
          title: 'Analytical cookies',
          description: 'Analytical cookies measure visit counts and collect information about traffic sources. We use them to improve the performance of our website. They help us understand how you navigate our site.',
        },
        retargeting: {
          title: 'Marketing cookies',
          description: 'These cookies allow our trusted partners to display more relevant messages tailored to your preferences.',
        },
      },
    },
    pl: {
      ok: 'Zaakceptuj wszystkie cookies',
      consentNotice: {
        title: 'Polityka dotycząca plików cookie',
        description: `
          <p>Korzystamy z plików cookie oraz podobnych rozwiązań technologicznych, zarówno własnych, jak i naszych partnerów. Obejmuje to zapisywanie, przechowywanie lub uzyskiwanie dostępu do informacji znajdujących się już w Twojej przeglądarce internetowej.</p>
          <p>Wykorzystujemy te technologie, aby umożliwić Ci prawidłowe działanie portalu Pomagam.pl, w tym zapewnić jego bezpieczeństwo oraz efektywność działania. Służą temu tzw.<b> niezbędne pliki cookie</b>, które pozostają zawsze aktywne. ${getIternalCookiePolicyElement(LEARN_MORE_PL)}</p>
          <p>Dodatkowo, używamy <b>opcjonalnych plików cookie</b> oraz podobnych technologii do celów analitycznych i retargetingowych. Możesz wyrazić zgodę na ich stosowanie lub jej odmówić. W dowolnym momencie masz możliwość zmiany swoich preferencji na stronie „Zarządzaj zgodami cookies” w serwisie Pomagam.pl. Opcjonalne pliki cookie wykorzystywane są w następujących celach:</p>
          <ol>
            <li><b>Analityka</b> – używamy tzw. plików cookie analitycznych, aby usprawnić działanie portalu Pomagam.pl. Dzięki nim możemy zrozumieć, jak użytkownicy poruszają się po naszej stronie – skąd trafiają na portal i jak długo z niego korzystają. Pozwala nam to na tworzenie statystyk oraz ich analizę, co wspiera dalszy rozwój serwisu. ${getIternalCookiePolicyElement(LEARN_MORE_PL)}</li>
            <li><b>Retargeting</b> – my oraz nasi zaufani partnerzy stosujemy pliki cookie i inne podobne technologie, aby prezentować użytkownikom określone treści, w tym dopasowane do ich zainteresowań informacje o Pomagam.pl, zwłaszcza dotyczące prowadzonych zbiórek. Takie treści mogą być wyświetlane zarówno na portalu Pomagam.pl, jak i na zewnętrznych stronach internetowych. ${getIternalCookiePolicyElement(LEARN_MORE_PL)}</li>
          </ol>
        `,
        learnMore: 'Dostosuj mój wybór',
      },
      consentModal: {
        title: 'Zarządzaj swoimi preferencjami',
        description: 'Masz możliwość w każdej chwili zmodyfikować lub odwołać udzieloną wcześniej zgodę na stosowanie opcjonalnych plików cookie.',
      },
      privacyPolicyUrl: COOKIE_POLICY_URL_PL,
      privacyPolicy: {
        name: 'Polityce stosowania plików cookies',
        text: 'Szczegółowe informacje znajdziesz w naszej {privacyPolicy}',
      },
      purposes: {
        internal: {
          title: 'Niezbędne pliki cookie',
          description: 'Te pliki są wymagane do prawidłowego funkcjonowania serwisu Pomagam.pl. Dzięki tym plikom możemy także zapewnić bezpieczeństwo korzystania z portalu oraz identyfikować i usuwać błędy techniczne.',
        },
        analytics: {
          title: 'Analityczne pliki cookie',
          description: 'Te pliki służą do zbierania danych statystycznych na temat odwiedzin i źródeł ruchu na stronie Pomagam.pl. Wykorzystujemy je, aby lepiej zrozumieć sposób korzystania z naszego serwisu i wprowadzać usprawnienia w jego działaniu.',        },
        retargeting: {
          title: 'Retargetingowe pliki cookie',
          description: 'Te pliki umożliwiają nam oraz naszym zaufanym partnerom prezentowanie trafniejszych, w tym spersonalizowanych komunikatów i treści reklamowych, dostosowanych do Twoich zainteresowań i aktywności. Komunikaty te dotyczą przede wszystkim prowadzonych w serwisie zbiórek i mogą być wyświetlane zarówno na stronie Pomagam.pl jak i w serwisach zewnętrznych.',
        },
      },
    },
  },

  /*
  Here you specify the third-party services that Klaro will manage for you.
  */
  services: [
    {
      name: 'pomagam',
      title: 'Pomagam.pl',
      translations: {
        en: {
          description: `
            The cookies we use are essential for remembering user preferences (including cookie consent), maintaining sessions (especially for navigation parameters, registration, login, and payment processing), ensuring site security (especially protection against fraud, authentication, and attack prevention), and correct content presentation.<br>
            ${getIternalCookiePolicyElement('Pomagam.pl Cookie Policy')}
          `,
        },
        pl: {
          description: `
            Wykorzystywane przez nas pliki cookie są niezbędne w celu zapamiętywania preferencji użytkownika (w tym zgody na wykorzystywanie plików cookie), do utrzymania sesji (w szczególności w celu zachowania parametrów nawigacyjnych, obsługi rejestracji, logowania i obsługi procesu płatności), zapewnienia bezpieczeństwa strony (w szczególności ochrony przed oszustwami, uwierzytelniania i zapobiegania atakom) oraz poprawnej prezentacji treści na stronie.<br>
            ${getIternalCookiePolicyElement('Polityka stosowania plików cookie Pomagam.pl')}
          `,
        },
      },
      required: true,
      default: true,
      /*
      If 'optOut' is set to 'true', Klaro will load this service even before the user
      has given explicit consent. We strongly advise against this.
      */
      optOut: false,
      purposes: ['internal'],
      onInit: `
        // initialization code here (will be executed only once per page-load)
        window.dataLayer = window.dataLayer || [];
        window.gtag = function(){dataLayer.push(arguments)}
        gtag('consent', 'default', {'analytics_storage': 'denied', 'ad_storage': 'denied', 'ad_user_data': 'denied', 'ad_personalization': 'denied'})
        gtag('set', 'ads_data_redaction', true)  // Has no effect once ad_storage is granted, so no need to set it to 'false' on 'google_ads' grant.
      `,
    },
    {
      name: 'cloudflare',
      title: 'Cloudflare',
      translations: {
        en: {
          description: `
            Cloudflare is responsible for ensuring the security and uninterrupted operation of our site. Cookies are used in this case to remember information about successful security checks. The cookies we use are essential for remembering user preferences (including cookie consent), maintaining sessions (especially for navigation parameters, registration, login, and payment processing), ensuring site security (especially protection against fraud, authentication, and attack prevention), and correct content presentation.<br>
            ${getCookiePolicyElement(
              'Cloudflare Privacy Policy',
              'https://www.cloudflare.com/privacypolicy/',
            )}
          `,
        },
        pl: {
          description: `
            Cloudflare odpowiada za zapewnienie bezpieczeństwa oraz nieprzerwanego działania naszej strony. Pliki cookie są w tym przypadku wykorzystywane do zapamiętania informacji o pomyślnym przejściu kontroli bezpieczeństwa.  Wykorzystywane przez nas pliki cookie są niezbędne w celu zapamiętywania preferencji użytkownika (w tym zgody na wykorzystywanie plików cookie), do utrzymania sesji (w szczególności w celu zachowania parametrów nawigacyjnych, obsługi rejestracji, logowania i obsługi procesu płatności), zapewnienia bezpieczeństwa strony (w szczególności ochrony przed oszustwami, uwierzytelniania i zapobiegania atakom) oraz poprawnej prezentacji treści na stronie.<br>
            ${getCookiePolicyElement(
              'Polityka prywatności Cloudflare',
              'https://www.cloudflare.com/pl-pl/privacypolicy/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'meta_facebook_login',
      title: 'Meta (Facebook)',
      translations: {
        en: {
          description: `
            Cookies are essential during registration or login to Pomagam.pl using a Facebook account. They are saved during the login process. Meta may use data about your activity on Pomagam.pl to supplement information within other Meta services, e.g., on Facebook. Privacy settings can be managed in ${getCookiePolicyElement('Meta Privacy Center', 'https://www.facebook.com/privacy/center/?locale=pl_PL')}<br>
            ${getCookiePolicyElement(
              'Meta Cookie Policy',
              'https://www.facebook.com/privacy/policies/cookies',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są niezbędne podczas rejestracji lub logowania do serwisu Pomagam.pl z użyciem konta Facebook. Są zapisywane w trakcie procesu logowania. Meta może wykorzystywać dane dotyczące Twojej aktywności w serwisie Pomagam.pl w celu uzupełnienia informacji w ramach innych usług Meta, np. na Facebooku. Zarządzanie ustawieniami prywatności możliwe jest w ${getCookiePolicyElement('Centrum ochrony prywatności Meta', 'https://www.facebook.com/privacy/center/?locale=pl_PL')}<br>
            ${getCookiePolicyElement(
              'Zasady stosowania plików cookie Meta',
              'https://www.facebook.com/privacy/policies/cookies?locale=pl_PL',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'zen',
      title: 'Zen',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Zen. They are saved during the payment process.<br>
            ${getCookiePolicyElement(
              'ZEN Cookie Policy',
              'https://www.zen.com/files/tc/cookies_policy.pdf',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Zen. Zapisywane są w czasie przechodzenia przez proces płatności.<br>
            ${getCookiePolicyElement(
              'Polityka cookies ZEN',
              'https://www.zen.com/files/tc/cookies_policy.pdf',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'stripe',
      title: 'Stripe',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Stripe. They are saved during the payment process.<br>
            ${getCookiePolicyElement(
              'Stripe Cookie Policy',
              'https://stripe.com/legal/cookies-policy',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Stripe. Zapisywane są w czasie przechodzenia przez proces płatności.<br>
            ${getCookiePolicyElement(
              'Polityka cookies Stripe',
              'https://stripe.com/en-pl/legal/cookies-policy',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'gpay',
      title: 'Google Pay',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Google Pay. They are saved during the payment process.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Google Pay. Zapisywane są w czasie przechodzenia przez proces płatności.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/intl/pl/privacy/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'apple_pay',
      title: 'Apple Pay',
      translations: {
        en: {
          description: `
            Cookies are used during payments made via Apple Pay. They are saved during the payment process.<br>
            ${getCookiePolicyElement(
              'Apple\'s use of cookies',
              'https://www.apple.com/legal/privacy/en-ww/cookies/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie są stosowane w trakcie płatności realizowanych przez Apple Pay. Zapisywane są w czasie przechodzenia przez proces płatności.<br>
            ${getCookiePolicyElement(
              'Korzystanie przez Apple z plików cookie',
              'https://www.apple.com/legal/privacy/pl/cookies/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'youtube',
      title: 'Youtube',
      translations: {
        en: {
          description: `
            Cookies used by YouTube enable the playback of videos embedded on the Pomagam.pl website. They are saved only when the user plays a video.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie stosowane przez YouTube w celu umożliwienia odtwarzania filmów zamieszczonych na stronie Pomagam.pl. Są one zapisywane tylko wtedy, gdy użytkownik uruchomi materiał wideo.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/intl/pl/privacy/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'vimeo',
      title: 'Vimeo',
      translations: {
        en: {
          description: `
            Cookies used by Vimeo enable the playback of videos embedded on the Pomagam.pl website. They are saved only when the user plays a video.<br>
            ${getCookiePolicyElement(
              'Vimeo Privacy Policy',
              'https://vimeo.com/privacy/',
            )}
          `,
        },
        pl: {
          description: `
            Pliki cookie stosowane przez Vimeo w celu umożliwienia odtwarzania filmów zamieszczonych na stronie Pomagam.pl. Są one zapisywane tylko wtedy, gdy użytkownik uruchomi materiał wideo.<br>
            ${getCookiePolicyElement(
              'Polityka prywatności Vimeo',
              'https://vimeo.com/privacy/',
            )}
          `,
        },
      },
      required: true,
      default: true,
      optOut: false,
      purposes: ['internal'],
    },
    {
      name: 'google_analytics',
      title: 'Google Analytics',
      translations: {
        en: {
          description: `
            These cookies allow us to collect and process data in the Google Analytics service.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w usłudze Google Analytics.<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/intl/pl/privacy/',
            )}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        /^_ga(_.*)?/,
        '_gid'
      ],
    },
    {
      name: 'clarity',
      title: 'Microsoft Clarity',
      translations: {
        en: {
          description: `
            These cookies allow us to collect and process data in the Microsoft Clarity service.<br>
            ${getCookiePolicyElement(
              'Microsoft Privacy Statement',
              'https://www.microsoft.com/en-us/privacy/privacystatement#maincookiessimilartechnologiesmodule',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w usłudze Microsoft Clarity.<br>
            ${getCookiePolicyElement(
              'Oświadczenie o ochronie prywatności w firmie Microsoft',
              'https://www.microsoft.com/pl-PL/privacy/privacystatement#maincookiessimilartechnologiesmodule',
            )}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        '_clck',
        '_clsk',
        '_cltk',
        'CLID',
        'ANONCHK',
        'MR',
        'MUID',
        'SM',
      ],
    },
    {
      name: 'log_rocket',
      title: 'LogRocket',
      translations: {
        en: {
          description: `
            These cookies allow us to collect and process data in the LogRocket service.<br>
            ${getCookiePolicyElement(
              'LogRocket Privacy Policy',
              'https://logrocket.com/privacy',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w usłudze LogRocket.<br>
            ${getCookiePolicyElement(
              'Polityka Prywatności LogRocket',
              'https://logrocket.com/privacy',
            )}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        /_lr_tabs_(-.*)?/,
        /_lr_hb_(-.*)?/,
      ],
    },
    {
      name: 'adc',
      translations: {
        en: {
          title: 'Ad Campaigns Tracking',
          description: `
            These cookies allow us to collect and process data based on our own solutions.<br>
            ${getIternalCookiePolicyElement('Pomagam.pl Cookie Policy')}
          `,
        },
        pl: {
          title: 'Pomagam.pl',
          description: `
            Te pliki pozwalają nam zbierać i przetwarzać dane w oparciu o własne rozwiązania.<br>
            ${getIternalCookiePolicyElement('Polityka stosowania plików cookie Pomagam.pl')}
          `,
        },
      },
      purposes: ['analytics'],
      cookies: [
        'acd',
      ],
    },
    {
      name: 'meta_conversion_api',
      title: 'Meta (Facebook)',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages in the Meta advertising network.<br>
            ${getCookiePolicyElement(
              'Meta Cookie Policy',
              'https://www.facebook.com/privacy/policies/cookies',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam na prezentowanie trafniejszych komunikatów w sieci reklamowej Meta<br>
            ${getCookiePolicyElement(
              'Zasady stosowania plików cookie Meta',
              'https://www.facebook.com/privacy/policies/cookies?locale=pl_PL',
            )}
          `,
        },
      },
      cookies: [
        '_fbc',
        '_fbp',
        /^fbssls_(.*)?/,
      ],
      purposes: ['retargeting'],
    },
    {
      name: 'google_ads',
      title: 'Google',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages in the Google advertising network.<br>
            ${getCookiePolicyElement(
              'How Google processes personal data',
              'https://business.safety.google/privacy/',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam na prezentowanie trafniejszych komunikatów w sieci reklamowej Google<br>
            ${getCookiePolicyElement(
              'Jak Google przetwarza dane osobowe',
              'https://business.safety.google/intl/pl/privacy/',
            )}
          `,
        },
      },
      cookies: [
        'TaxCampaign2025Visited',
        /^_gcl_(.*)?/,
      ],
      purposes: ['retargeting'],
    },
    {
      name: 'tiktok',
      title: 'TikTok',
      translations: {
        en: {
          description: `
            These cookies allow us to display more relevant messages in the TikTok advertising network.<br>
            ${getCookiePolicyElement(
              'TikTok Cookie Policy',
              'https://ads.tiktok.com/help/article/using-cookies-with-tiktok-pixel',
            )}
          `,
        },
        pl: {
          description: `
            Te pliki pozwalają nam na prezentowanie trafniejszych komunikatów w sieci reklamowej TikTok<br>
            ${getCookiePolicyElement(
              'Polityka cookies TikTok',
              'https://ads.tiktok.com/help/article/using-cookies-with-tiktok-pixel?lang=pl-PL',
            )}
          `,
        },
      },
      cookies: [
        '_ttp',
        '_tt_enable_cookie',
        /^ttcsid_(.*)?/,
        '_pangle',
        'ttcsid',
        'tt_sessionId',
        'tt_appInfo',
      ],
      purposes: ['retargeting'],
    },
  ],

  /*
  You can define an optional callback function that will be called each time the
  consent state for any given service changes. The consent value will be passed as
  the first parameter to the function (true=consented). The `service` config will
  be passed as the second parameter.
  */
  callback: function(consent, service) {
    // DEVS: This is a workaround for default Klaro behavior, which calls onDecline hooks on the initial load,
    // when cookies preferences are not set yet.
    // Google Consent Mode requires that only a default consent is sent until user decides to accept or decline consent.
    // Klaro calls `onDecline` hooks on initial load (when no consents are chosen yet), so a custom handling is required.
    const name = service.name;
    if (name in serviceHooks) {
      // Check previously granted Klaro consent for the service (stored in cookies).
      const consentState = hasGrantedKlaroConsent(name);
      if (consentState === undefined) {
        // Consent not set yet, so hooks are not called.
        return;
      }
      consentState ? serviceHooks[name].onAccept() : serviceHooks[name].onDecline();
    }
  },
};

// Google Consent Mode V2 consent types
const ANALYTICS_STORAGE = 'analytics_storage';
const AD_STORAGE = 'ad_storage';
const AD_USER_DATA = 'ad_user_data';
const AD_PERSONALIZATION = 'ad_personalization';

function updateGoogleConsents(isGranted, consentTypes = []) {
  // Updates Google Consent Mode V2 consents to a single value for all provided consent types.
  // The value is 'granted' or 'denied' based on the isGranted parameter.
  // Requires GoogleTagManager to be loaded and initialized as `gtag`.
  const value = isGranted ? 'granted' : 'denied';
  const consents = consentTypes.reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {});
  // Update GTM consents.
  gtag('consent', 'update', consents);
}

// Custom service hooks for consents that require special onAccept/onDecline handling.
const serviceHooks = {
  google_analytics: {
    onAccept: () => {
      if (hasGrantedKlaroConsent('google_ads')) {
        // GoogleAnalytics permission accepted hook is called BEFORE GoogleAds, so let's skip sending the event for now.
        return;
      } else {
        // GoogleAds is not granted, so consent update for GoogleAnalytics can be safely sent.
        updateGoogleConsents(true, [ANALYTICS_STORAGE]);
      }
    },
    onDecline: () => {
      // Google Consent Mode V2 update: analytics storage
      updateGoogleConsents(false, [ANALYTICS_STORAGE]);
    },
  },
  google_ads: {
    onAccept: () => {
      // Google Consent Mode V2 update
      // DEVS WARNING: This code leverages permission check order to ensure that GoogleAnalytics x GoogleAds combo
      // is handled accordingly to specs: only one gtag consent update is sent.

      // Check if GoogleAnalytics had been granted, but sending a consent update has been postponed.
      // In such case, both services require one single content update call.
      // Otherwise, only GoogleAds consents are being updated.
      let consents = [AD_STORAGE, AD_USER_DATA, AD_PERSONALIZATION];
      if (hasGrantedKlaroConsent('google_analytics')) {
        consents.push(ANALYTICS_STORAGE);
      }
      updateGoogleConsents(true, consents);
    },
    onDecline: () => {
      // Google Consent Mode V2 update: ad storage and personalization
      // Decline hooks are called separately for each service.
      updateGoogleConsents(false, [AD_STORAGE, AD_USER_DATA, AD_PERSONALIZATION]);
    },
  },
  log_rocket: {
    onAccept: () => {
      const LOG_ROCKET_SCRIPT_URL = 'https://cdn.lgrckt-in.com/LogRocket.min.js';
      if (!window.logRocketData) {
        return;
      }

      function logRocketInit(data) {
        // Start LogRocket session with optional user identification
        window.LogRocket.init(data.id, { release: data.release });
        if (data.user) {
          window.LogRocket.identify(data.user.id, {
            // Disabled for privacy reasons until it's discussed and decided.
            // name: data.user.name,
            // email: data.user.email,
            type: data.user.type,
          });
        }
      }
      // Script is loaded asynchronously to avoid blocking the page load.
      // LogRocket.init and LogRocket.identify are called after the script is loaded to ensure LogRocket is available.
      loadScript(LOG_ROCKET_SCRIPT_URL, function() {
        // try/catch is used to handle known 'Java is gone' error in Facebook in-app browser.
        try {
          const data = window.logRocketData || {};
          logRocketInit(data);
        } catch (e) {
          console.warn('Error initializing LogRocket:', e);
        }
      });
    },
    onDecline: () => {},
  },
  adc: {
    onAccept: () => {
      const ADC_PAGE_CLASS = 'opt-acd'; // Class for pages where ADC is enabled
      // Load only on dedicated pages
      if ($('#page').hasClass(ADC_PAGE_CLASS)) {
        AdCampaignDataToCookie(true);
      }
    },
    onDecline: () => {},
  },
  tiktok: {
    onAccept: () => dataLayer.push({ event: 'klaro-tiktok-accepted' }),
    onDecline: () => dataLayer.push({ event: 'klaro-tiktok-declined' }),
  },
  clarity: {
    onAccept: () => dataLayer.push({ event: 'klaro-clarity-accepted' }),
    onDecline: () => dataLayer.push({ event: 'klaro-clarity-declined' }),
  },
};

function hasGrantedKlaroConsent(value) {
  // Checks if klaro consent is granted for the given value.
  const cookies_consent = JSON.parse(getCookie(COOKIE_NAME) || '{}');
  return cookies_consent[value];
}

// Set the language for Klaro based on the Django language settings in js/jsi18n/<lang>/djangojs.js
// Language presence in that file is a custom override of the output of Django's 'makemessages -all -d djangojs' command.
// See fabfile.py for more details.
klaroConfig.lang = window.django.lang in klaroConfig.translations ? window.django.lang : 'en';

export function setupKlaro() {
  // Initialize Klaro with the configuration
  klaro.setup(klaroConfig);
}

export function showKlaro() {
  klaro.show(klaroConfig);
}

$(document).ready(() => {
  // Load Klaro consent manager
  setupKlaro();
  window.showKlaro = showKlaro;
});
